/**
 * Copyright © 2026 Adnuntius AS.
 */
import {Component, DestroyRef, Inject, inject, signal} from '@angular/core';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {TranslateService} from '@ngx-translate/core';
import {FormsModule} from '@angular/forms';
import {TranslateModule} from '@ngx-translate/core';
import {ButtonsModule} from 'ngx-bootstrap/buttons';
import {finalize} from 'rxjs';
import * as _ from 'lodash';
import template from './ruleset-edit-page.component.html';
import {AdnSelectSearchElement} from '../common/directives/adn-select/adn-select.component';
import {SignalAdminResourceEditPageBase} from '../common/controller/signal-admin-resource-edit-page-base';
import {ADN_TARGETING_TYPES} from '../targeting/targeting-constants';
import {LocalNetworkInfo} from '../../components/session/local-network-info';
import {LocalNetworkProfile} from '../../components/session/local-network-profile';
import {LocalUserPermissions} from '../../components/session/local-user-permissions';
import {layoutCategoriesFor} from './ruleset-layout-categories';
import {RulesetApiService, RulesetModel} from './ruleset-api.service';
import {AppSharedModule} from '../common/app-shared.module';
import {AdnSelectComponent} from '../common/directives/adn-select/adn-select.component';
import {FormControlsModule} from '../common/directives/form-controls/form-controls.module';
import {BlocklistEditUsageComponent} from '../blocklist/blocklist-edit-usage.component';

interface AdvertiserFloorPrice {
  type?: 'ADVERTISER' | 'TEAM';
  advertiser?: any;
  team?: any;
  floorPrice?: {amount?: number | null; currency?: string | null};
}

@Component({
  standalone: true,
  imports: [AppSharedModule, FormsModule, TranslateModule, ButtonsModule, AdnSelectComponent, FormControlsModule, BlocklistEditUsageComponent],
  selector: 'adn-ruleset-edit-page',
  template
})
export class RulesetEditPageComponent extends SignalAdminResourceEditPageBase<RulesetModel> {
  readonly rulesetTypes = ['INVENTORY', 'TARGETING', 'SIZE', 'LAYOUT'];
  readonly targetingTypes = _.values(ADN_TARGETING_TYPES);
  readonly layoutCategories = ['FULL_PAGE', 'POLITICAL', 'HTML', 'VAST', 'NATIVE', 'THIRD_PARTY', 'STANDARD', 'DATA', 'IMAGE'];

  private readonly allTeamsState = signal<any[] | null>(null);
  private readonly allLayoutsState = signal<any[]>([]);
  private readonly layoutNamesState = signal<Record<string, any>>({});
  private readonly advertiserSearchResultsState = signal<any[]>([]);
  private readonly advertiserTeamSearchResultsState = signal<any[]>([]);
  private readonly singularTeamState = signal<any>(null);

  get allTeams(): any[] | null { return this.allTeamsState(); }
  set allTeams(value: any[] | null) { this.allTeamsState.set(value); }
  get allLayouts(): any[] { return this.allLayoutsState(); }
  set allLayouts(value: any[]) { this.allLayoutsState.set(value); }
  get layoutNames(): Record<string, any> { return this.layoutNamesState(); }
  set layoutNames(value: Record<string, any>) { this.layoutNamesState.set(value); }
  get advertiserSearchResults(): any[] { return this.advertiserSearchResultsState(); }
  set advertiserSearchResults(value: any[]) { this.advertiserSearchResultsState.set(value); }
  get advertiserTeamSearchResults(): any[] { return this.advertiserTeamSearchResultsState(); }
  set advertiserTeamSearchResults(value: any[]) { this.advertiserTeamSearchResultsState.set(value); }
  get singularTeam(): any { return this.singularTeamState(); }
  set singularTeam(value: any) { this.singularTeamState.set(value); }
  readonly teamsLoading = signal(true);
  readonly teamsLoadFailed = signal(false);
  readonly layoutsLoading = signal(true);
  readonly layoutsLoadFailed = signal(false);
  canUpdateSiteRuleset = false;
  teamsWarning = false;
  showSingular = true;
  includeHighImpact = false;
  showHighImpact = false;
  layoutsExpanded = false;
  layoutCategoriesExpanded = false;

  private readonly rulesetApi = inject(RulesetApiService);
  private readonly rulesetDestroyRef = inject(DestroyRef);

  constructor(
    @Inject(LocalNetworkProfile) private readonly networkProfile: LocalNetworkProfile,
    @Inject(LocalUserPermissions) LocalUserPermissions: any,
    private readonly translate: TranslateService,
    private readonly localNetworkInfo: LocalNetworkInfo = new LocalNetworkInfo()
  ) {
    super(inject(RulesetApiService), 'ruleset');
    this.canUpdateSiteRuleset = LocalUserPermissions.hasNetwork('AX_PUBLISHER_ADMIN')
      || LocalUserPermissions.hasAny('AX_PUBLISHER_TEAM_ADMIN')
      || LocalUserPermissions.hasAny('MANAGE_PUBLISHING');

    this.rulesetApi.listTeams().pipe(
      finalize(() => this.teamsLoading.set(false)),
      takeUntilDestroyed(this.rulesetDestroyRef)
    ).subscribe({
      next: (page) => {
        this.allTeams = _.filter(page.results, (team: any) => {
          return team.type === 'STANDARD' || team.type === 'AX_PUBLISHER';
        });
        if (this.isNew && this.allTeams.length === 1 && !this.singularTeam) {
          this.singularTeam = this.allTeams[0];
        }
        this.resolveSingularTeamReference();
      },
      error: () => {
        this.allTeams = [];
        this.teamsLoadFailed.set(true);
      }
    });

    this.rulesetApi.listLayouts().pipe(
      finalize(() => this.layoutsLoading.set(false)),
      takeUntilDestroyed(this.rulesetDestroyRef)
    ).subscribe({
      next: (page) => {
        this.allLayouts = _.map(page.results || [], (layout: any) => this.enrichLayoutWithCategories(layout));
        this.layoutNames = _.keyBy(this.allLayouts, 'id');
      },
      error: () => this.layoutsLoadFailed.set(true)
    });
  }

  protected override initialiseModel(model: RulesetModel): RulesetModel {
    const initialised = super.initialiseModel(model);
    if (this.isNew) {
      initialised.type = initialised.type || 'INVENTORY';
    }
    this.initialiseFloorPrices(initialised);
    this.initialiseUiSizes(initialised);
    this.ensureTeamSelections(initialised);
    this.normaliseTeamsAndLayouts(initialised);
    this.resolveSingularTeamReference(initialised);
    this.layoutsExpanded = this.isNew;
    this.layoutCategoriesExpanded = this.isNew;
    return initialised;
  }

  protected override afterSave(model: RulesetModel): void {
    const previousTeamCount = (this.editingModel.teams || []).length;
    this.teamsWarning = previousTeamCount !== (model.teams || []).length;
  }

  /** API returns team/layout objects; the form binds ids. Runs on load, save and undo. */
  private normaliseTeamsAndLayouts(model: RulesetModel): void {
    const toId = (value: any) => _.isString(value) ? value : _.get(value, 'id');
    const firstTeam = _.get(model, ['teams', 0]) || null;
    model.teams = _.compact(_.map(model.teams, toId));
    model.layouts = _.compact(_.map(model.layouts, toId));
    if (!this.isNew || model.teams.length) {
      this.singularTeam = firstTeam;
      this.showSingular = model.teams.length <= 1;
    }
  }

  protected override prepareForSubmit(model: RulesetModel): void {
    model.advertiserFloorPrices = _.filter(model.advertiserFloorPrices, (fp: AdvertiserFloorPrice) => {
      const type = _.get(fp, 'type');
      if (_.get(fp, ['floorPrice', 'amount']) >= 0 && _.get(fp, ['floorPrice', 'currency', 'length']) === 3 && type) {
        if (type === 'ADVERTISER') {
          return _.get(fp, ['advertiser', 'id']);
        }
        if (type === 'TEAM') {
          return _.get(fp, ['team', 'id']);
        }
      }
      return false;
    });
    model.advertiserFloorPrices = _.map(model.advertiserFloorPrices, (fp: AdvertiserFloorPrice) => {
      const type = _.get(fp, 'type');
      if (type === 'ADVERTISER' && fp.team) {
        delete fp.team;
      } else if (type === 'TEAM' && fp.advertiser) {
        delete fp.advertiser;
      }
      return fp;
    });
    model.sizes = _.filter(model.uiSizes, (size: any) => {
      const dimensions = _.get(size, 'dimensions');
      if (dimensions) {
        return _.get(dimensions, 'width') || (_.isArray(dimensions) && dimensions.length === 2);
      }
      return false;
    });
    if (this.includeHighImpact) {
      model.sizes.push({dimensions: [0, 0]});
    }
    if (!_.has(model, ['floorPrice', 'amount']) || _.get(model, ['floorPrice', 'amount']) === null) {
      model.floorPrice = null;
    }
    if (this.showSingular) {
      model.teams = this.singularTeam ? [this.singularTeam] : [];
    }
  }

  onFieldChange(): void {
    this.markExternalFormDirty();
  }

  get visibleLayoutCategories(): string[] {
    return this.layoutCategories.filter((category) => this.isLayoutCategoryVisible(category));
  }

  private enrichLayoutWithCategories(layout: any): any {
    const categories = layoutCategoriesFor(layout);
    const categorySummary = _.map(categories, (category) => {
      return this.translate.instant('layoutCategory.' + category);
    }).join(', ');
    const categoryHelp = categorySummary
      ? `${this.translate.instant('label.layoutCategory')}: ${categorySummary}`
      : '';
    return {
      ...layout,
      categories,
      categoryHelp
    };
  }

  private isLayoutCategoryVisible(category: string): boolean {
    if ((this.editingModel.layoutCategories || []).includes(category)) {
      return true;
    }
    switch (category) {
      case 'DATA':
        return false;
      case 'FULL_PAGE':
        return this.networkProfile.isPureMarketplacePlatform();
      case 'POLITICAL':
        return this.networkProfile.isPoliticalAdsPlatform();
      default:
        return true;
    }
  }

  selectTeams(): void {
    this.editingModel.teams = _.map(this.allTeams || [], 'id');
    this.onFieldChange();
  }

  deselectTeams(): void {
    this.editingModel.teams = [];
    this.onFieldChange();
  }

  removeFloorPrices(index: number): void {
    _.pullAt(this.editingModel.advertiserFloorPrices, index);
    this.initialiseFloorPrices();
    this.onFieldChange();
  }

  addFloorPrices(): void {
    this.editingModel.advertiserFloorPrices = this.editingModel.advertiserFloorPrices || [];
    this.editingModel.advertiserFloorPrices.push({type: 'ADVERTISER'});
    this.onFieldChange();
  }

  removeDimensions(index: number): void {
    _.pullAt(this.editingModel.uiSizes, index);
    this.initialiseUiSizes();
    this.onFieldChange();
  }

  addDimensions(): void {
    this.editingModel.uiSizes = this.editingModel.uiSizes || [];
    this.editingModel.uiSizes.push({dimensions: []});
    this.onFieldChange();
  }

  advertiserSearch = (searchElement: AdnSelectSearchElement): void => {
    this.rulesetApi.searchAdvertisers(searchElement.search).pipe(
      takeUntilDestroyed(this.rulesetDestroyRef)
    ).subscribe({
      next: (results) => {
        this.advertiserSearchResults = results;
      },
      error: () => {
        this.advertiserSearchResults = [];
      }
    });
  };

  advertiserTeamSearch = (searchElement: AdnSelectSearchElement): void => {
    this.rulesetApi.searchAdvertiserTeams(searchElement.search).pipe(
      takeUntilDestroyed(this.rulesetDestroyRef)
    ).subscribe({
      next: (results) => {
        this.advertiserTeamSearchResults = results;
      },
      error: () => {
        this.advertiserTeamSearchResults = [];
      }
    });
  };

  canRemoveFloorPrice(index: number, advPrice: AdvertiserFloorPrice): boolean {
    return !(index === 0
      && (this.editingModel.advertiserFloorPrices || []).length < 2
      && !advPrice?.advertiser);
  }

  private ensureTeamSelections(model: RulesetModel = this.editingModel): void {
    model.teams = _.isArray(model.teams) ? model.teams : [];
    model.layouts = _.isArray(model.layouts) ? model.layouts : [];
    model.layoutCategories = _.isArray(model.layoutCategories) ? model.layoutCategories : [];
    model.targetingTypes = _.isArray(model.targetingTypes) ? model.targetingTypes : [];
  }

  private initialiseFloorPrices(model: RulesetModel = this.editingModel): void {
    model.advertiserFloorPrices = _.isArray(_.get(model, 'advertiserFloorPrices'))
      ? _.get(model, 'advertiserFloorPrices')
      : [];
    model.advertiserFloorPrices[0] = model.advertiserFloorPrices[0] ? model.advertiserFloorPrices[0] : {};
    model.advertiserFloorPrices = _.map(model.advertiserFloorPrices, (advFloor: AdvertiserFloorPrice) => {
      if (_.get(advFloor, ['team', 'id'])) {
        advFloor.type = 'TEAM';
      } else {
        advFloor.type = 'ADVERTISER';
      }
      return advFloor;
    });
  }

  private initialiseUiSizes(model: RulesetModel = this.editingModel): void {
    const sizes = _.isArray(_.get(model, 'sizes')) ? _.get(model, 'sizes') : [];
    sizes[0] = sizes[0] ? sizes[0] : {dimensions: []};
    model.uiSizes = _.cloneDeep(sizes);
    const highImpactIndex = _.findIndex(model.uiSizes, (uiSize: any) => {
      return uiSize.dimensions?.[0] === 0 && uiSize.dimensions?.[1] === 0;
    });
    this.includeHighImpact = highImpactIndex > -1;
    if (highImpactIndex > -1) {
      _.pullAt(model.uiSizes, highImpactIndex);
    }
    const hasFullPageLayouts = this.localNetworkInfo.getFullPageLayoutIds().length > 0;
    this.showHighImpact = hasFullPageLayouts || this.includeHighImpact;
  }

  private resolveSingularTeamReference(model: RulesetModel = this.editingModel): void {
    if (!this.showSingular || !this.allTeams?.length) {
      return;
    }
    if (this.singularTeam && !_.isString(this.singularTeam)) {
      const match = _.find(this.allTeams, ['id', this.singularTeam.id]);
      if (match) {
        this.singularTeam = match;
      }
      return;
    }
    const teamId = this.singularTeam || _.get(model, ['teams', 0]);
    if (!teamId) {
      return;
    }
    const resolvedId = _.isString(teamId) ? teamId : teamId.id;
    const match = _.find(this.allTeams, ['id', resolvedId]);
    if (match) {
      this.singularTeam = match;
    }
  }
}
